<template>
  <div v-if="visible" class="inline-block">
    <div
      class="flex items-center gap-1 tag-item"
      :style="{ backgroundColor: currentBgColor, color: textColor }"
      @mouseenter="darkenBackground"
      @mouseleave="resetBackground"
    >
      <div class="text-center truncate">
        <slot>{{ text }}</slot>
      </div>
      <div
        v-if="xIcon && !noIcon"
        data-svg-wrapper
        class="flex items-center cursor-pointer"
      >
        <button
          type="button"
          class="inline-flex items-center ms-1 text-sm text-opacity-70 bg-transparent rounded-xs hover:bg-opacity-20 dark:hover:bg-opacity-30"
          aria-label="Remove"
          @click="dismissTag"
        >
          <svg
            class="w-2 h-2"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 14 14"
          >
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
            />
          </svg>
          <span class="sr-only">Remove tag</span>
        </button>
        <svg
          v-if="!xIcon && !noIcon"
          width="10"
          height="10"
          viewBox="0 0 10 10"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M5 2C5.55228 2 6 1.55228 6 1C6 0.447715 5.55228 0 5 0C4.44772 0 4 0.447715 4 1C4 1.55228 4.44772 2 5 2Z"
            :fill="textColor"
          />
          <path
            d="M5 6C5.55228 6 6 5.55228 6 5C6 4.44772 5.55228 4 5 4C4.44772 4 4 4.44772 4 5C4 5.55228 4.44772 6 5 6Z"
            :fill="textColor"
          />
          <path
            d="M5 10C5.55228 10 6 9.55229 6 9C6 8.44772 5.55228 8 5 8C4.44772 8 4 8.44772 4 9C4 9.55229 4.44772 10 5 10Z"
            :fill="textColor"
          />
        </svg>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref } from "vue";

export default {
  props: {
    text: {
      type: String,
      default: ""
    },
    backgroundColor: {
      type: String,
      required: true
    },
    textColor: {
      type: String,
      required: true
    },
    xIcon: {
      type: Boolean,
      default: false
    },
    noIcon: {
      type: Boolean,
      default: false
    }
  },
  emits: ["delete"],
  setup(props, { emit }) {
    const visible = ref(true);
    const isHovered = ref(false);

    const currentBgColor = computed(() => {
      if (!isHovered.value) {
        return props.backgroundColor;
      }

      // Darken the background color by 15%
      return darkenColor(props.backgroundColor, 0.15);
    });

    const dismissTag = () => {
      visible.value = false;
      emit("delete");
    };

    const darkenBackground = () => {
      isHovered.value = true;
    };

    const resetBackground = () => {
      isHovered.value = false;
    };

    // Function to darken a color by a percentage
    const darkenColor = (color, amount) => {
      // Handle hex colors
      if (color.startsWith("#")) {
        return darkenHex(color, amount);
      }

      // Handle rgb/rgba colors
      if (color.startsWith("rgb")) {
        return darkenRgb(color, amount);
      }

      // Default case - return original color if format is not supported
      return color;
    };

    // Darken hex color
    const darkenHex = (hex, amount) => {
      // Remove the # if present
      hex = hex.replace("#", "");

      // Convert to RGB
      let r = parseInt(hex.substring(0, 2), 16);
      let g = parseInt(hex.substring(2, 4), 16);
      let b = parseInt(hex.substring(4, 6), 16);

      // Darken each component
      r = Math.max(0, Math.floor(r * (1 - amount)));
      g = Math.max(0, Math.floor(g * (1 - amount)));
      b = Math.max(0, Math.floor(b * (1 - amount)));

      // Convert back to hex
      return `#${r.toString(16).padStart(2, "0")}${g
        .toString(16)
        .padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
    };

    // Darken rgb/rgba color
    const darkenRgb = (rgb, amount) => {
      // Extract values
      const matches = rgb.match(
        /(\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d*(?:\.\d+)?))?/
      );
      if (!matches) return rgb;

      let r = parseInt(matches[1]);
      let g = parseInt(matches[2]);
      let b = parseInt(matches[3]);
      const a = matches[4] !== undefined ? parseFloat(matches[4]) : 1;

      // Darken each component
      r = Math.max(0, Math.floor(r * (1 - amount)));
      g = Math.max(0, Math.floor(g * (1 - amount)));
      b = Math.max(0, Math.floor(b * (1 - amount)));

      // Return the new color
      return a < 1 ? `rgba(${r}, ${g}, ${b}, ${a})` : `rgb(${r}, ${g}, ${b})`;
    };

    return {
      visible,
      currentBgColor,
      darkenBackground,
      resetBackground,
      dismissTag
    };
  }
};
</script>

<style scoped>
.tag-item {
  padding: 0.15rem 0.375rem;
  border-radius: 0.375rem;
  display: inline-flex;
  align-items: center;
  transition: background-color 0.2s ease;
}
</style>
